import { Application, IDList } from './Application';
import { ConfigOptions } from './config';
import { Module } from './Module';

import { toCamel } from './utils/string';

export type Hook = ()  => void;

interface HookTypes {
	after : Hook[]
	end : Hook[]
}

export class Sandbox {
	public application : Application;
	public config : ConfigOptions;

	private hooks : HookTypes = {
		after: [],
		end: [],
	};

	private doneAfterHooks = 0;

	/**
	 * Initializes the Sandbox.
	 *
	 * @method init
	 * @constructor
	 * @param {Applicaton} application
	 *      The application reference
	 * @param {Object} config
	 *      The configuration
	 */
	constructor(application : Application, config : ConfigOptions) {
		this.application = application;
		this.config = config;
	}

	/**
	 * Adds (register and start) all modules in the given context scope.
	 *
	 * @method addModules
	 * @param {jQuery} $ctx
	 *      The jQuery context
	 * @return {Array}
	 *      A list containing the references of the registered modules
	 */
	public addModules($ctx? : JQuery) : Module[] {
		let modules : Module[] = [];
		const application = this.application;

		if ($ctx) {
			// Register modules
			modules = application.registerModules($ctx);

			// Start modules
			application.start(modules);
		}

		return modules;
	}

	/**
	 * Removes a module by module instances.
	 * This stops and unregisters a module through a module instance.
	 *
	 * @method removeModules
	 * @param {mixed} modules
	 *      A list containing the module instances to remove or the jQuery context to look for registered modules in.
	 */
	public removeModules(modules : Module[] | JQuery) {
		const application = this.application;

		if (!$.isArray(modules)) {
			const $ctx : JQuery = modules;

			// Get modules
			const tmpModules : Module[] = [];

			const $candidates = $ctx.find('.mod').add($ctx.hasClass('mod') ? $ctx : $());

			$candidates.each((_, elem) => {
				// Check for instance
				// Modified by Raphael Schweikert (taascraa): add ability to have multiple modules on a single element
				const ids : IDList = $(elem).data('terrific-ids') || {};

				for(const modName in ids) {
					const module = this.getModuleById(ids[modName]);

					if (module) {
						tmpModules.push(module);
					}
				}
			});

			modules = tmpModules;
		}

		if (modules) {
			// Stop modules
			application.stop(modules);

			// Unregister modules
			application.unregisterModules(modules);
		}
	}

	/**
	 * Subscribes a module to a connector.
	 *
	 * @method subscribe
	 * @param {String} connector The full connector name (e.g. MasterSlave-Navigation)
	 * @param {Module} module The module instance
	 */
	public subscribe(connector : string, module : Module) {
		// Explicitly cast connector to string (e.g. to handle 0 case)
		connector = String(connector);

		// Guards
		if(!module || !connector) {
			throw new Error('subscribe is expecting 2 parameters (connector, module)');
		}

		if(!(module instanceof Module)) {
			throw new Error('the module param must be an instance of Tc.Module');
		}

		this.application.registerConnection(connector, module);
	}

	/**
	 * Unsubscribes a module from a connector.
	 *
	 * @method unsubscribe
	 * @param {String} connectorId The connector channel id (e.g. 2 or Navigation)
	 * @param {Module} module The module instance
	 */
	public unsubscribe(connectorId : string, module : Module) {
		if (module instanceof Module && connectorId) {
			// Explicitly cast connector id to string
			this.application.unregisterConnection(String(connectorId), module);
		}
	}



	/**
	 * Gets the appropriate module for the given ID.
	 *
	 * @method getModuleById
	 * @param {int} id
	 *      The module ID
	 * @return {Module}
	 *      The appropriate module
	 */
	public getModuleById(id : number) {
		const application = this.application;

		if (application.modules[id]) {
			return application.modules[id];
		}

		throw new Error(`the module with the id ${id} does not exist`);
	}

	/**
	 * Gets the appropriate module for the given DOM element
	 *
	 * @method getModuleInstance
	 * @param {HTMLElement} element
	 *      The module DOM element
	 * @param {String} [modName]
	 *      The module name (relevant if multiple Modules are defined on the same DOM element)
	 *      Schema: Classname without "Tc.Module." - eg. "BrightcoveVideo"
	 * @return {Module}
	 *      The appropriate module
	 */
	// Added by Swisscom (Dave Talamona, tzhtada2):
	// Prevent modules from using other modules internal "terrific-ids" for getting their module instance
	public getModuleInstance(element : HTMLElement | JQuery, moduleName? : string) : Module | null {
		const application = this.application;
		const ids : IDList = $(element).data('terrific-ids') || {};

		let id : number | undefined;
		if(moduleName) {
			let modName = moduleName;
			if (modName.indexOf('-') > -1) {
				modName = toCamel(modName);
			}
			modName = modName.substr(0, 1).toUpperCase() + modName.substr(1);

			if(!(modName in ids)) {
				return null;
			}
			id = ids[modName];
		} else {
			// If no modName is given, return the first module found
			for(const name in ids) {
				id = ids[name];
				break;
			}
		}

		if(id === undefined) {
			return null;
		}

		return application.modules[id] || null;
	}

	/**
	 * Gets all modules for the given DOM element
	 *
	 * @method getModuleInstances
	 * @param {HTMLElement} element
	 *      The module DOM element
	 * @return {Module}[]
	 *      The appropriate modules
	 */
	// Added by Swisscom (Raphael Schweikert, taascraa)
	public getModuleInstances(element : HTMLElement | JQuery) {
		const ids : IDList = $(element).data('terrific-ids') || {};

		const results = [];
		for(const modName in ids) {
				const module = this.getModuleInstance(element, modName);
				if(module) {
					results.push(module);
				}
			}

		return results;
	}

	/**
	 * Gets the application config.
	 *
	 * @method getConfig
	 * @return {Object}
	 *      The configuration object
	 */
	public getConfig() : ConfigOptions {
		return this.config;
	}

	/**
	 * Gets an application config param.
	 *
	 * @method getConfigParam
	 * @param {String} name
	 *      The param name
	 * @return {mixed}
	 *      The appropriate configuration param
	 */
	public getConfigParam(name : string) {
		const config = this.config;

		if (config[name] !== undefined) {
			return config[name];
		}

		throw new Error(`the config param ${name} does not exist`);
	}

	/**
	 * Adds a callback to be executed in the appropriate phase.
	 *
	 * @method addCallback
	 * @param {String} phase default: end
	 * @param {Function?} callback
	 */
	public addCallback(phase : keyof HookTypes, callback : Hook) : void;
	public addCallback(callback : Hook) : void;
	public addCallback(phase : keyof HookTypes | Hook, callback? : Hook) {
		if (typeof phase === 'function') {
			// Only 1 param
			callback = phase;
			phase = 'end';
		}

		if(!callback) {
			return;
		}

		const callbacks = this.hooks[phase];

		callbacks.push(callback);
	}

	/**
	 * Collects the module status messages and handles the callbacks.
	 * This means that it is ready for the 'after' hook.
	 *
	 * @method ready
	 * @param {Function} callback
	 *      The 'after' hook module callback
	 */
	public ready(callback : Hook) {
		const afterHooks = this.hooks.after;

		// Add the callback to the stack
		afterHooks.push(callback);

		// Check whether all modules are ready for the 'after' hook
		if (this.application.modules.length !== (afterHooks.length + this.doneAfterHooks)) {
			return;
		}

		for (let i = 0; i < afterHooks.length; i++) {
			// Make sure the callback is only executed once (and is not called during addModules)
			const [afterCallback] = afterHooks.splice(i, 1); i--;
			afterCallback();
			this.doneAfterHooks++;
		}

		// Execute the end hooks
		const endHooks = this.hooks.end;
		for (const hook of endHooks) {
			hook();
		}
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/vendor/terrific/Sandbox.ts